import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { jobApiService, Job, JobEditUpdate } from '../../recruiter/services/jobApi';
import { COMPANY_ROUTES } from '../constants';

/**
 * Read directly from write-layer fields (root + hardCriteria + softCriteria).
 * No aiParsedData resolution — parse layer is immutable after LLM parse.
 */
const getResolvedFields = (job: Job) => ({
    title: job.title || '',
    description: job.description || '',
    location: typeof job.location === 'string' ? (job.location || '') : '',
    employmentType: job.employmentType || '',
    experienceLevel: job.experienceLevel || '',
    salaryRange: job.salaryRange || '',
    education: job.requirements?.education || '',
    workMode: job.remote === true ? 'Remote' : (job.aiParsedData?.work_mode || (job.remote === false ? 'On-site' : '')),
    requiredSkills: job.hardCriteria?.requiredSkills || [],
    preferredSkills: job.softCriteria?.preferredSkills || [],
    keywords: job.softCriteria?.keywords || [],
    requiredDegrees: job.hardCriteria?.requiredDegrees || [],
    benefits: job.benefits || [],
    responsibilities: job.aiParsedData?.responsibilities || [],
    minYearsExperience: job.hardCriteria?.minYearsExperience || 0,
});

// Dropdown option constants
const EXPERIENCE_LEVEL_OPTIONS = [
    'Entry-level',
    'Junior',
    'Mid-level',
    'Senior',
    'Lead',
    'Principal',
    'Executive',
];

const EMPLOYMENT_TYPE_OPTIONS = [
    'Full-time',
    'Part-time',
    'Contract',
    'Freelance',
    'Internship',
    'Temporary',
];

const WORK_MODE_OPTIONS = [
    'Remote',
    'On-site',
    'Hybrid',
];

const EDUCATION_OPTIONS = [
    'No Requirement',
    'High School Diploma',
    "Associate's Degree",
    "Bachelor's Degree",
    "Master's Degree",
    'Doctoral Degree / PhD',
    'Professional Degree (MD, JD, etc.)',
];

const COUNTRIES = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
    'France', 'India', 'China', 'Japan', 'Singapore', 'Brazil', 'Mexico',
    'Netherlands', 'Sweden', 'Switzerland', 'South Korea', 'Israel',
    'United Arab Emirates', 'Ireland', 'New Zealand', 'Other',
];

/** Tag input component for skills/keywords/benefits editing */
const TagInput: React.FC<{
    tags: string[];
    onAdd: (tag: string) => void;
    onRemove: (index: number) => void;
    placeholder: string;
    colorClass: string;
}> = ({ tags, onAdd, onRemove, placeholder, colorClass }) => {
    const [input, setInput] = useState('');

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
            e.preventDefault();
            onAdd(input.trim());
            setInput('');
        }
        if (e.key === 'Backspace' && !input && tags.length > 0) {
            onRemove(tags.length - 1);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-2 w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus-within:ring-2 focus-within:ring-[#015FA6] bg-white min-h-[48px]">
            {tags.map((tag, i) => (
                <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium ${colorClass}`}>
                    {tag}
                    <button type="button" onClick={() => onRemove(i)} className="hover:opacity-70">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </span>
            ))}
            <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tags.length === 0 ? placeholder : 'Add more...'}
                className="flex-1 min-w-[120px] outline-none text-base text-[#0B111E] bg-transparent"
                style={{ fontFamily: 'Manrope, sans-serif' }}
            />
        </div>
    );
};

const CompanyJobDetailsNew: React.FC = () => {
    const { jobId } = useParams<{ jobId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [job, setJob] = useState<Job | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<Partial<JobEditUpdate>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [editLocationCountry, setEditLocationCountry] = useState('');
    const [editLocationCity, setEditLocationCity] = useState('');

    const locationState = location.state as any;
    const projectId = locationState?.projectId;

    useEffect(() => {
        if (jobId) fetchJobDetails();
    }, [jobId]);

    const fetchJobDetails = async () => {
        try {
            setLoading(true);
            const fetchedJob = await jobApiService.getJobById(jobId!);
            setJob(fetchedJob);
        } catch (err) {
            console.error('Failed to fetch job:', err);
            setError('Failed to load job details');
        } finally {
            setLoading(false);
        }
    };

    const enterEditMode = () => {
        if (!job) return;
        const fields = getResolvedFields(job);

        // Parse location string into city/country (e.g. "San Francisco, United States")
        const locParts = fields.location.split(',').map(s => s.trim());
        const parsedCity = locParts.length > 1 ? locParts.slice(0, -1).join(', ') : locParts[0] || '';
        const parsedCountry = locParts.length > 1 ? locParts[locParts.length - 1] : '';
        // Match against known countries, otherwise put it in "Other"
        const matchedCountry = COUNTRIES.includes(parsedCountry) ? parsedCountry : (parsedCountry ? 'Other' : '');
        setEditLocationCity(parsedCity);
        setEditLocationCountry(matchedCountry);

        setEditData({
            title: fields.title,
            description: fields.description,
            location: fields.location,
            employmentType: fields.employmentType,
            experienceLevel: fields.experienceLevel,
            salaryRange: fields.salaryRange,
            education: fields.education,
            workMode: fields.workMode,
            requiredSkills: [...fields.requiredSkills],
            preferredSkills: [...fields.preferredSkills],
            keywords: [...fields.keywords],
            requiredDegrees: [...fields.requiredDegrees],
            benefits: [...fields.benefits],
            responsibilities: [...fields.responsibilities],
            minYearsExperience: fields.minYearsExperience,
        });
        setSaveError(null);
        setSaveSuccess(false);
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setIsEditing(false);
        setEditData({});
        setSaveError(null);
    };

    const handleSave = async () => {
        if (!jobId || !job) return;
        try {
            setIsSaving(true);
            setSaveError(null);

            // Compose location from city + country before diffing
            const composedLocation = [editLocationCity, editLocationCountry].filter(Boolean).join(', ');

            // Only send fields that actually changed (avoid writing unchanged legacy data to ai_parsed_data)
            const orig = getResolvedFields(job);
            const changed: Partial<JobEditUpdate> = {};

            if (editData.title !== orig.title) changed.title = editData.title;
            if (editData.description !== orig.description) changed.description = editData.description;
            if (composedLocation !== orig.location) changed.location = composedLocation;
            if (editData.employmentType !== orig.employmentType) changed.employmentType = editData.employmentType;
            if (editData.experienceLevel !== orig.experienceLevel) changed.experienceLevel = editData.experienceLevel;
            if (editData.salaryRange !== orig.salaryRange) changed.salaryRange = editData.salaryRange;
            if (editData.education !== orig.education) changed.education = editData.education;
            if (editData.workMode !== orig.workMode) changed.workMode = editData.workMode;
            if ((editData.minYearsExperience || 0) !== orig.minYearsExperience) changed.minYearsExperience = editData.minYearsExperience;
            if (JSON.stringify(editData.requiredSkills) !== JSON.stringify(orig.requiredSkills)) changed.requiredSkills = editData.requiredSkills;
            if (JSON.stringify(editData.preferredSkills) !== JSON.stringify(orig.preferredSkills)) changed.preferredSkills = editData.preferredSkills;
            if (JSON.stringify(editData.keywords) !== JSON.stringify(orig.keywords)) changed.keywords = editData.keywords;
            if (JSON.stringify(editData.requiredDegrees) !== JSON.stringify(orig.requiredDegrees)) changed.requiredDegrees = editData.requiredDegrees;
            if (JSON.stringify(editData.benefits) !== JSON.stringify(orig.benefits)) changed.benefits = editData.benefits;
            if (JSON.stringify(editData.responsibilities) !== JSON.stringify(orig.responsibilities)) changed.responsibilities = editData.responsibilities;

            if (Object.keys(changed).length === 0) {
                setIsEditing(false);
                setEditData({});
                return;
            }

            const updatedJob = await jobApiService.updateJob(jobId, changed);
            setJob(updatedJob);
            setIsEditing(false);
            setEditData({});
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (err: any) {
            console.error('Failed to update job:', err);
            setSaveError(err?.message || 'Failed to save changes. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = <K extends keyof JobEditUpdate>(key: K, value: JobEditUpdate[K]) => {
        setEditData(prev => ({ ...prev, [key]: value }));
    };

    const addTag = (field: 'requiredSkills' | 'preferredSkills' | 'keywords' | 'requiredDegrees' | 'benefits' | 'responsibilities', tag: string) => {
        const current = (editData[field] as string[]) || [];
        if (!current.includes(tag)) {
            updateField(field, [...current, tag]);
        }
    };

    const removeTag = (field: 'requiredSkills' | 'preferredSkills' | 'keywords' | 'requiredDegrees' | 'benefits' | 'responsibilities', index: number) => {
        const current = (editData[field] as string[]) || [];
        updateField(field, current.filter((_, i) => i !== index));
    };

    // Loading state
    if (loading) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-lg font-medium text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>Loading job details...</p>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    // Error state
    if (error || !job) {
        return (
            <CompanyLayout>
                <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-[#0B111E] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Job Not Found</h2>
                        <p className="text-base text-[#757575] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>{error || 'The requested job could not be found.'}</p>
                        <button
                            onClick={() => navigate(COMPANY_ROUTES.JOB_POSTINGS)}
                            className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Back to Jobs
                        </button>
                    </div>
                </div>
            </CompanyLayout>
        );
    }

    const fields = getResolvedFields(job);

    return (
        <CompanyLayout>
            {/* Back Button */}
            <button
                onClick={() => {
                    if (projectId) {
                        navigate(COMPANY_ROUTES.PROJECT_DETAILS(projectId));
                    } else {
                        navigate(COMPANY_ROUTES.PROJECTS);
                    }
                }}
                className="flex items-center gap-2 text-[#015FA6] hover:opacity-70 transition-opacity mb-6"
            >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M12 15L7 10L12 5" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {projectId ? 'Back to Project' : 'Back to Projects'}
                </span>
            </button>

            {/* Page Header */}
            <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                    {isEditing ? (
                        <input
                            value={editData.title || ''}
                            onChange={(e) => updateField('title', e.target.value)}
                            className="w-full text-2xl font-semibold text-[#0B111E] px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6]"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            placeholder="Job Title"
                        />
                    ) : (
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-semibold text-[#0B111E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {fields.title || 'Untitled Job'}
                            </h1>
                            <span className="px-3 py-1 bg-[#F0FFF4] text-[#16A34A] text-xs font-semibold rounded-full" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Active
                            </span>
                        </div>
                    )}
                </div>
                {!isEditing && (
                    <button
                        onClick={enterEditMode}
                        className="flex items-center gap-2 px-4 py-2.5 border border-[#015FA6] text-[#015FA6] font-medium rounded-lg hover:bg-[#F0F7FF] transition-colors ml-4"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.388 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.38308 14.4084 2.61182C14.5032 2.84056 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05493C14.3137 4.28367 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Edit
                    </button>
                )}
            </div>

            {/* Success Banner */}
            {saveSuccess && (
                <div className="mb-4 px-4 py-3 bg-[#F0FFF4] border border-[#BBF7D0] rounded-lg flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#16A34A" strokeWidth="1.5" />
                        <path d="M7 10L9 12L13 8" stroke="#16A34A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-sm font-medium text-[#16A34A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Changes saved successfully. Embeddings are being regenerated.
                    </span>
                </div>
            )}

            {/* Save Error Banner */}
            {saveError && (
                <div className="mb-4 px-4 py-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" stroke="#DC2626" strokeWidth="1.5" />
                        <path d="M10 6V10M10 14H10.01" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <span className="text-sm font-medium text-[#DC2626]" style={{ fontFamily: 'Manrope, sans-serif' }}>{saveError}</span>
                </div>
            )}

            {/* Info Bar */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] px-6 py-4 mb-4">
                {isEditing ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Country</label>
                            <select
                                value={editLocationCountry}
                                onChange={(e) => setEditLocationCountry(e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                <option value="">Select country...</option>
                                {COUNTRIES.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>City</label>
                            <input
                                value={editLocationCity}
                                onChange={(e) => setEditLocationCity(e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                                placeholder="e.g. San Francisco"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Employment Type</label>
                            <select
                                value={editData.employmentType || ''}
                                onChange={(e) => updateField('employmentType', e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                <option value="">Select...</option>
                                {EMPLOYMENT_TYPE_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Experience Level</label>
                            <select
                                value={editData.experienceLevel || ''}
                                onChange={(e) => updateField('experienceLevel', e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                <option value="">Select...</option>
                                {EXPERIENCE_LEVEL_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Work Mode</label>
                            <select
                                value={editData.workMode || ''}
                                onChange={(e) => updateField('workMode', e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm bg-white"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                <option value="">Select...</option>
                                {WORK_MODE_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Salary Range</label>
                            <input
                                value={editData.salaryRange || ''}
                                onChange={(e) => updateField('salaryRange', e.target.value)}
                                className="w-full px-3 py-2.5 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-sm"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                                placeholder="e.g. $80,000 - $120,000"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-6 flex-wrap">
                        {fields.location && (
                            <div className="flex items-center gap-2 text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 2C6.13 2 3 5.13 3 9C3 12.17 7 17 10 17C13 17 17 12.17 17 9C17 5.13 13.87 2 10 2Z" stroke="#757575" strokeWidth="1.5" />
                                    <circle cx="10" cy="9" r="2" stroke="#757575" strokeWidth="1.5" />
                                </svg>
                                {fields.location}
                            </div>
                        )}
                        <div className="flex items-center gap-2 text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="#757575" strokeWidth="1.5" />
                                <path d="M10 6V10L13 13" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            {fields.employmentType || 'Full-time'}
                        </div>
                        {fields.salaryRange && (
                            <div className="flex items-center gap-2 text-sm font-semibold text-[#015FA6]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                    <circle cx="10" cy="10" r="7" stroke="#015FA6" strokeWidth="1.5" />
                                    <path d="M10 6V7M10 13V14M12 9C12 8.45 11.55 8 11 8H9.5C8.95 8 8.5 8.45 8.5 9C8.5 9.55 8.95 10 9.5 10H10.5C11.05 10 11.5 10.45 11.5 11C11.5 11.55 11.05 12 10.5 12H9C8.45 12 8 11.55 8 11" stroke="#015FA6" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                {fields.salaryRange}
                            </div>
                        )}
                        {fields.experienceLevel && (
                            <div className="flex items-center gap-2 text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                    <path d="M4 7H16M4 7V15C4 16.1046 4.89543 17 6 17H14C15.1046 17 16 16.1046 16 15V7M4 7L6 3H14L16 7" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {fields.experienceLevel}
                            </div>
                        )}
                        {fields.workMode && (
                            <div className="flex items-center gap-2 text-sm text-[#757575]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                                    <path d="M3 10C3 10 5 4 10 4C15 4 17 10 17 10C17 10 15 16 10 16C5 16 3 10 3 10Z" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="10" cy="10" r="2.5" stroke="#757575" strokeWidth="1.5" />
                                </svg>
                                {fields.workMode}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Job Description Card */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 mb-4">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Job Description
                </h2>
                {isEditing ? (
                    <textarea
                        value={editData.description || ''}
                        onChange={(e) => updateField('description', e.target.value)}
                        rows={8}
                        className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base text-[#0B111E] resize-y"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        placeholder="Enter job description..."
                    />
                ) : (
                    <p className="text-base text-[#515151] whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {fields.description || 'No description provided.'}
                    </p>
                )}
            </div>

            {/* Skills & Keywords Card */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 mb-4">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Skills & Keywords
                </h2>

                {/* Required Skills */}
                <div className="mb-5">
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Required Skills</h3>
                    {isEditing ? (
                        <TagInput
                            tags={editData.requiredSkills || []}
                            onAdd={(tag) => addTag('requiredSkills', tag)}
                            onRemove={(i) => removeTag('requiredSkills', i)}
                            placeholder="Type a skill and press Enter..."
                            colorClass="bg-[#F0F7FF] text-[#015FA6]"
                        />
                    ) : fields.requiredSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {fields.requiredSkills.map((skill, i) => (
                                <span key={i} className="px-3 py-1.5 bg-[#F0F7FF] text-[#015FA6] rounded-lg text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {skill}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-[#A5A5A5]" style={{ fontFamily: 'Manrope, sans-serif' }}>None specified</p>
                    )}
                </div>

                {/* Preferred Skills */}
                <div className="mb-5">
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Preferred Skills</h3>
                    {isEditing ? (
                        <TagInput
                            tags={editData.preferredSkills || []}
                            onAdd={(tag) => addTag('preferredSkills', tag)}
                            onRemove={(i) => removeTag('preferredSkills', i)}
                            placeholder="Type a skill and press Enter..."
                            colorClass="bg-[#F0FFF4] text-[#16A34A]"
                        />
                    ) : fields.preferredSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {fields.preferredSkills.map((skill, i) => (
                                <span key={i} className="px-3 py-1.5 bg-[#F0FFF4] text-[#16A34A] rounded-lg text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {skill}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-[#A5A5A5]" style={{ fontFamily: 'Manrope, sans-serif' }}>None specified</p>
                    )}
                </div>

                {/* Keywords */}
                <div>
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Keywords</h3>
                    {isEditing ? (
                        <TagInput
                            tags={editData.keywords || []}
                            onAdd={(tag) => addTag('keywords', tag)}
                            onRemove={(i) => removeTag('keywords', i)}
                            placeholder="Type a keyword and press Enter..."
                            colorClass="bg-[#EDE9FE] text-[#6B21A8]"
                        />
                    ) : fields.keywords.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {fields.keywords.map((kw, i) => (
                                <span key={i} className="px-3 py-1.5 bg-[#EDE9FE] text-[#6B21A8] rounded-lg text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {kw}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-[#A5A5A5]" style={{ fontFamily: 'Manrope, sans-serif' }}>None specified</p>
                    )}
                </div>
            </div>

            {/* Requirements Card */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 mb-4">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Requirements
                </h2>
                {isEditing ? (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Minimum Years of Experience</label>
                            <input
                                type="number"
                                value={editData.minYearsExperience || 0}
                                onChange={(e) => updateField('minYearsExperience', parseInt(e.target.value) || 0)}
                                className="w-full max-w-[200px] px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                                min={0}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Education</label>
                            <select
                                value={editData.education || ''}
                                onChange={(e) => updateField('education', e.target.value)}
                                className="w-full px-4 py-3 border border-[#A5A5A5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#015FA6] text-base bg-white"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                <option value="">Select education level...</option>
                                {EDUCATION_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#757575] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Required Degrees</label>
                            <TagInput
                                tags={editData.requiredDegrees || []}
                                onAdd={(tag) => addTag('requiredDegrees', tag)}
                                onRemove={(i) => removeTag('requiredDegrees', i)}
                                placeholder="Type a degree and press Enter..."
                                colorClass="bg-[#FFF8F0] text-[#D97706]"
                            />
                        </div>
                    </div>
                ) : (fields.minYearsExperience > 0 || fields.education || fields.requiredDegrees.length > 0) ? (
                    <div className="bg-[#FFF8F0] border border-[#FFE4CC] rounded-xl p-5 space-y-3">
                        {fields.minYearsExperience > 0 && (
                            <div className="flex items-start gap-2.5">
                                <span className="text-[#D97706] font-bold text-lg leading-none mt-0.5">&#8226;</span>
                                <span className="text-sm text-[#78350F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Minimum {fields.minYearsExperience} years of experience
                                </span>
                            </div>
                        )}
                        {fields.education && (
                            <div className="flex items-start gap-2.5">
                                <span className="text-[#D97706] font-bold text-lg leading-none mt-0.5">&#8226;</span>
                                <span className="text-sm text-[#78350F]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fields.education}</span>
                            </div>
                        )}
                        {fields.requiredDegrees.length > 0 && (
                            <div className="flex items-start gap-2.5">
                                <span className="text-[#D97706] font-bold text-lg leading-none mt-0.5">&#8226;</span>
                                <span className="text-sm text-[#78350F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Required degrees: {fields.requiredDegrees.join(', ')}
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-[#A5A5A5]" style={{ fontFamily: 'Manrope, sans-serif' }}>No specific requirements listed.</p>
                )}
            </div>

            {/* Work Details & Benefits Card */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6 mb-4">
                <h2 className="text-lg font-semibold text-[#0B111E] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Work Details & Benefits
                </h2>

                {/* Work Type */}
                {fields.workMode && !isEditing && (
                    <div className="mb-5">
                        <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Work Mode</h3>
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EDE9FE] text-[#6B21A8] rounded-lg text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                <path d="M2 6H14M2 6V12C2 13.1046 2.89543 14 4 14H12C13.1046 14 14 13.1046 14 12V6M2 6L4 2H12L14 6" stroke="#6B21A8" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {fields.workMode}
                        </span>
                    </div>
                )}

                {/* Benefits */}
                <div>
                    <h3 className="text-sm font-medium text-[#757575] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Benefits & Perks</h3>
                    {isEditing ? (
                        <TagInput
                            tags={editData.benefits || []}
                            onAdd={(tag) => addTag('benefits', tag)}
                            onRemove={(i) => removeTag('benefits', i)}
                            placeholder="Type a benefit and press Enter..."
                            colorClass="bg-[#F0FFF4] text-[#16A34A]"
                        />
                    ) : fields.benefits.length > 0 ? (
                        <ul className="space-y-2">
                            {fields.benefits.map((benefit, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <svg className="w-5 h-5 text-[#16A34A] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-sm text-[#515151]" style={{ fontFamily: 'Manrope, sans-serif' }}>{benefit}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-[#A5A5A5]" style={{ fontFamily: 'Manrope, sans-serif' }}>No benefits listed.</p>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl border border-[#EDEDED] p-6">
                {isEditing ? (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            {isSaving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </button>
                        <button
                            onClick={cancelEdit}
                            disabled={isSaving}
                            className="px-6 py-3 border border-[#A5A5A5] text-[#757575] font-medium rounded-lg hover:bg-[#F4F4F4] transition-colors disabled:opacity-50"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-3">
                        <button
                            onClick={enterEditMode}
                            className="px-6 py-3 bg-[#015FA6] text-white font-medium rounded-lg hover:bg-[#014a85] transition-colors"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            Edit Job
                        </button>
                        <button
                            onClick={() => navigate(COMPANY_ROUTES.RECEIVED_APPLICATIONS)}
                            className="px-6 py-3 border border-[#015FA6] text-[#015FA6] font-medium rounded-lg hover:bg-[#F0F7FF] transition-colors"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                            View Applications
                        </button>
                    </div>
                )}
            </div>
        </CompanyLayout>
    );
};

export default CompanyJobDetailsNew;
